/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Exocet Mobile Menu Motion — GSAP takeover and accordion polish
 */
(function () {
  "use strict";

  var BOUND = "data-mobile-motion-bound";
  var docBound = false;
  var activeTimeline = null;
  var accordionTweens = new WeakMap();

  function supports() {
    return typeof gsap !== "undefined" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  }

  function cascadeTargets(menu) {
    return Array.prototype.slice
      .call(
        menu.querySelectorAll(
          ".mobile-menu-2026__header, .mobile-menu-2026__search, .mobile-menu-2026__item, .mobile-menu-2026__outlet, .mobile-menu-2026__util, .mobile-menu-2026__region",
        ),
      )
      .filter(function (el) {
        return el.offsetParent !== null || el.classList.contains("mobile-menu-2026__header");
      });
  }

  function getPanelHeight(panel) {
    var inner = panel ? panel.querySelector(".mobile-menu-2026__panel-inner") : null;
    return inner ? inner.scrollHeight : 0;
  }

  function initPanel(panel) {
    if (!panel) return;
    var item = panel.closest(".mobile-menu-2026__item");
    var isOpen = item && item.classList.contains("is-open");
    gsap.set(panel, {
      height: isOpen ? "auto" : 0,
      overflow: "hidden",
    });
    var initInner = panel.querySelectorAll(".mobile-menu-2026__panel-inner > *");
    if (initInner.length) {
      gsap.set(initInner, {
        autoAlpha: 1,
        y: isOpen ? 0 : -8,
      });
    }
  }

  function animatePanel(panel, open) {
    if (!panel) return;

    var previous = accordionTweens.get(panel);
    if (previous) previous.kill();

    var innerItems = panel.querySelectorAll(".mobile-menu-2026__panel-inner > *");
    var tl = gsap.timeline({
      defaults: { overwrite: "auto" },
      onComplete: function () {
        gsap.set(panel, { height: open ? "auto" : 0 });
        accordionTweens.delete(panel);
      },
    });

    if (open) {
      tl.set(panel, { height: 0, overflow: "hidden" });
      tl.to(panel, { height: getPanelHeight(panel), duration: 0.42, ease: "power3.out" }, 0);
      if (innerItems.length) {
        tl.fromTo(innerItems, { y: -8 }, { y: 0, duration: 0.36, stagger: 0.035, ease: "power2.out" }, 0.08);
      }
    } else {
      if (innerItems.length) {
        tl.to(innerItems, { y: -8, duration: 0.18, stagger: 0.012, ease: "power2.in" }, 0);
      }
      tl.to(panel, { height: 0, duration: 0.3, ease: "power2.inOut" }, 0.04);
    }

    accordionTweens.set(panel, tl);
  }

  function openMenu(menu) {
    if (activeTimeline) activeTimeline.kill();

    var drawer = menu.querySelector("[data-mobile-menu-drawer]");
    var overlay = menu.querySelector("[data-mobile-menu-overlay]");
    if (!drawer || !overlay) return;

    var items = cascadeTargets(menu);

    // Ask the header scroll-motion instance to show itself. The instance is stored
    // on the header element as _scrollMotion and exposes a public showHeader() method.
    // We do NOT call gsap directly on the wrapper — that would fight the scroll-motion
    // tween and leave the header stuck at yPercent:0 after the menu closes.
    var headerEl = document.querySelector("[data-header-2026]");
    if (headerEl && headerEl._scrollMotion && typeof headerEl._scrollMotion.showHeader === "function") {
      headerEl._scrollMotion.showHeader();
    }

    activeTimeline = gsap.timeline({ defaults: { overwrite: "auto" } });
    activeTimeline.set(menu, { autoAlpha: 1, pointerEvents: "auto" }, 0);
    activeTimeline.set(overlay, { autoAlpha: 1 }, 0);
    activeTimeline.fromTo(
      drawer,
      { yPercent: -2, scale: 0.992, autoAlpha: 0 },
      { yPercent: 0, scale: 1, autoAlpha: 1, duration: 0.48, ease: "expo.out" },
      0.02,
    );
    activeTimeline.fromTo(
      items,
      { autoAlpha: 0, y: 18 },
      { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.045, ease: "power3.out" },
      0.12,
    );
  }

  function closeMenu(menu) {
    if (activeTimeline) activeTimeline.kill();

    var drawer = menu.querySelector("[data-mobile-menu-drawer]");
    var overlay = menu.querySelector("[data-mobile-menu-overlay]");
    if (!drawer || !overlay) return;

    var items = cascadeTargets(menu).reverse();

    activeTimeline = gsap.timeline({ defaults: { overwrite: "auto" } });
    activeTimeline.to(items, { autoAlpha: 0, y: 10, duration: 0.18, stagger: 0.01, ease: "power2.in" }, 0);
    activeTimeline.to(drawer, { yPercent: -1.5, autoAlpha: 0, duration: 0.28, ease: "power2.in" }, 0.05);
    activeTimeline.to(overlay, { autoAlpha: 0, duration: 0.28, ease: "power2.in" }, 0.05);
    activeTimeline.set(menu, { pointerEvents: "none" });
  }

  function boot(root) {
    if (!supports()) return;

    var menu = (root || document).querySelector("[data-mobile-menu]");
    if (!menu || menu.hasAttribute(BOUND)) return;
    menu.setAttribute(BOUND, "");

    document.documentElement.classList.add("mobile-menu-motion-ready");
    gsap.set(menu, { autoAlpha: menu.classList.contains("open") ? 1 : 0 });
    gsap.set(menu.querySelector("[data-mobile-menu-drawer]"), { autoAlpha: menu.classList.contains("open") ? 1 : 0 });
    menu.querySelectorAll("[data-accordion-panel]").forEach(initPanel);

    // Document listeners bound once; resolve the menu at dispatch time so
    // theme-editor re-renders do not stack stale closures over detached menus.
    if (!docBound) {
      docBound = true;

      document.addEventListener("mobile-menu:opened", function () {
        var m = document.querySelector("[data-mobile-menu]");
        if (!m) return;
        window.requestAnimationFrame(function () {
          if (m.classList.contains("open")) openMenu(m);
        });
      });

      document.addEventListener("mobile-menu:close", function () {
        var m = document.querySelector("[data-mobile-menu]");
        if (m) closeMenu(m);
      });
    }

    menu.addEventListener("click", function (event) {
      var toggle = event.target.closest("[data-accordion-toggle]");
      if (!toggle || !menu.contains(toggle)) return;
      var panel = document.getElementById(toggle.getAttribute("aria-controls"));
      window.requestAnimationFrame(function () {
        animatePanel(panel, toggle.getAttribute("aria-expanded") === "true");
      });
    });
  }

  ready(function () {
    boot(document);
  });

  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", function (event) {
      boot(event.target);
    });
  }

  window.ExocetMobileMenuMotion = {
    refresh: function () {
      var menu = document.querySelector("[data-mobile-menu]");
      if (menu && supports()) menu.querySelectorAll("[data-accordion-panel]").forEach(initPanel);
    },
    boot: boot,
  };

  if (window.ExocetMotion) {
    ExocetMotion.register(window.ExocetMobileMenuMotion);
  }
})();
